import type { DateFormat } from '@/lib/format';
import { DEFAULT_PALETTE_ID } from '@/lib/palette';
import type { AltitudeUnit, DistanceUnit, SpeedUnit } from '@/lib/units';
import { DEFAULT_UNIT_PREFERENCES } from '@/lib/units';

/**
 * Everything a pilot can set, and the `SettingsRepository` key each one lives
 * under.
 *
 * One row per key (`SettingsRepository`'s own doc explains why), so this file
 * is the map from a stable string key to a typed field and its default —
 * every other feature that reads or writes a setting goes through
 * `useSettings`/`useSetting` below rather than the repository directly, so a
 * key never gets spelled two different ways in two different files.
 */
export interface AppSettings {
  altitudeUnit: AltitudeUnit;
  speedUnit: SpeedUnit;
  distanceUnit: DistanceUnit;
  dateFormat: DateFormat;
  /** `'device'`, or an IANA zone id — see `format.ts`. */
  timeZone: string;
  defaultPaletteId: string;
}

export const SETTINGS_KEYS = {
  altitudeUnit: 'units.altitude',
  speedUnit: 'units.speed',
  distanceUnit: 'units.distance',
  dateFormat: 'display.dateFormat',
  timeZone: 'display.timeZone',
  defaultPaletteId: 'display.defaultPaletteId',
} as const satisfies Record<keyof AppSettings, string>;

export const DEFAULT_SETTINGS: AppSettings = {
  altitudeUnit: DEFAULT_UNIT_PREFERENCES.altitude,
  speedUnit: DEFAULT_UNIT_PREFERENCES.speed,
  distanceUnit: DEFAULT_UNIT_PREFERENCES.distance,
  dateFormat: 'nz',
  timeZone: 'device',
  defaultPaletteId: DEFAULT_PALETTE_ID,
};

/** A short, curated list rather than every IANA zone — this is a New Zealand logbook app first. */
export const TIME_ZONE_OPTIONS: readonly { id: string; label: string }[] = [
  { id: 'device', label: "This device's timezone" },
  { id: 'Pacific/Auckland', label: 'New Zealand (Auckland)' },
  { id: 'Pacific/Chatham', label: 'New Zealand (Chatham Islands)' },
  { id: 'Australia/Sydney', label: 'Australia (Sydney)' },
  { id: 'UTC', label: 'UTC' },
];
